import { useState, FormEvent } from 'react';

export default function WaitlistFormSection() {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    income: '',
    creditProfile: '',
    propertyType: '',
    moveInTimeframe: '',
    transactionalConsent: false,
    marketingConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.transactionalConsent) {
      alert('Please consent to receive transactional messages to continue.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('address', formData.address);
      formBody.append('phone', formData.phone);
      formBody.append('income', formData.income);
      formBody.append('creditProfile', formData.creditProfile);
      formBody.append('propertyType', formData.propertyType);
      formBody.append('moveInTimeframe', formData.moveInTimeframe);
      formBody.append('transactionalConsent', formData.transactionalConsent ? 'Yes' : 'No');
      formBody.append('marketingConsent', formData.marketingConsent ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d5rkksihs78ip5370j7g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          address: '',
          phone: '',
          income: '',
          creditProfile: '',
          propertyType: '',
          moveInTimeframe: '',
          transactionalConsent: false,
          marketingConsent: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Join Our Rental Waitlist
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Be the first to know about available rental properties. Fill out the form below and we'll contact you when we have homes that match your needs.
          </p>
        </div>

        <div className="bg-gray-50 p-8 md:p-12 rounded-lg shadow-sm">
          <form onSubmit={handleSubmit} data-readdy-form id="rental-waitlist-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(915) 555-0123"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="address" className="block text-sm font-semibold text-gray-900 mb-2">
                Current Address *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="123 Main St, El Paso, TX 79901"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="income" className="block text-sm font-semibold text-gray-900 mb-2">
                  Approximate Monthly Income *
                </label>
                <input
                  type="text"
                  id="income"
                  name="income"
                  value={formData.income}
                  onChange={(e) => setFormData({ ...formData, income: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="$3,000"
                />
              </div>

              <div>
                <label htmlFor="creditProfile" className="block text-sm font-semibold text-gray-900 mb-2">
                  Credit Profile *
                </label>
                <select
                  id="creditProfile"
                  name="creditProfile"
                  value={formData.creditProfile}
                  onChange={(e) => setFormData({ ...formData, creditProfile: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select your credit range</option>
                  <option value="below-560">Below 560</option>
                  <option value="560-600">560-600</option>
                  <option value="600-700">600-700</option>
                  <option value="700+">700+</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="propertyType" className="block text-sm font-semibold text-gray-900 mb-2">
                  What are you looking for? *
                </label>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select property type</option>
                  <option value="house">House</option>
                  <option value="duplex">Duplex</option>
                  <option value="apartment">Apartment</option>
                </select>
              </div>

              <div>
                <label htmlFor="moveInTimeframe" className="block text-sm font-semibold text-gray-900 mb-2">
                  When are you looking to move in? *
                </label>
                <select
                  id="moveInTimeframe"
                  name="moveInTimeframe"
                  value={formData.moveInTimeframe}
                  onChange={(e) => setFormData({ ...formData, moveInTimeframe: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select timeframe</option>
                  <option value="immediately">Immediately</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="2-3-months">2-3 months</option>
                  <option value="3-6-months">3-6 months</option>
                  <option value="6+-months">6+ months</option>
                </select>
              </div>
            </div>

            <div className="mb-6 space-y-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="transactionalConsent"
                  name="transactionalConsent"
                  checked={formData.transactionalConsent}
                  onChange={(e) => setFormData({ ...formData, transactionalConsent: e.target.checked })}
                  required
                  className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="transactionalConsent" className="ml-3 text-sm text-gray-700 cursor-pointer">
                  <strong>Required:</strong> I consent to receive transactional messages such as account alerts, reminders, or notifications from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="marketingConsent"
                  name="marketingConsent"
                  checked={formData.marketingConsent}
                  onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                  className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="marketingConsent" className="ml-3 text-sm text-gray-700 cursor-pointer">
                  <strong>Optional:</strong> I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-center font-semibold">
                  Thank you! You've been added to our waitlist. We'll contact you soon with available properties.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-center font-semibold">
                  Something went wrong. Please try again or call us at (915) 555-0123.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
